import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/ledger-cards';

// ============================================
// Ledger cards („Kartice"), read from the AB Soft ledger
// ============================================

const fileNameFrom = (disposition = '', fallback) => {
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/** Companies whose cards the user may open. @returns {Promise<Array<{_id, name}>>} */
export const getCompanies = async () => {
  const response = await axiosClient.get(`${API_BASE}/companies`);
  return response.data.data;
};

/** Years held, accounts, projects (org units) of a company. */
export const getOptions = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/options`);
  return response.data.data;
};

/** Partners booked on an account and/or org unit. @param {Object} params - { account?, orgUnit? } */
export const getPartners = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/partners`, { params });
  return response.data.data;
};

/**
 * One card: head, opening / period / total blocks and a page of lines.
 *
 * @param {Object} params - { from, to, account?, partner?, orgUnit?, offset, limit }
 */
export const getCard = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/card`, { params });
  return response.data.data;
};

/**
 * One page of the card's received, not yet booked invoices.
 *
 * @param {Object} params - the card's filters with { offset, limit }
 */
export const getUnbooked = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/card/unbooked`, { params });
  return response.data.data;
};

/**
 * The card as Excel or PDF, labelled in the user's language.
 *
 * @param {Object} filters - { from, to, account?, partner?, orgUnit? }
 * @param {'xlsx'|'pdf'} format
 * @param {Object} labels - translated labels
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export const exportCard = async (companyId, filters, format, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/card/export`,
    { filters, format, labels },
    { responseType: 'blob', timeout: 5 * 60 * 1000 }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], `Kartica.${format}`),
  };
};
